import mustache from "mustache";
import { ClienteFincaBaseResponse } from "../../../../modulos/terceros/response/ClienteFincaBaseResponse";
import { TareaEjecucionRequest } from "../../../../modulos/rastro/request/TareaEjecucionRequest";
import { TareaEjecucionResponse } from "../../../../modulos/rastro/response/TareaEjecucionResponse";
import { IFormBodyRender } from "../../../definitions/form/IFormBodyRender";
import { IFormField } from "../../../definitions/form/IFormField";
import { SummaryItemRender } from "../../../implementations/summary/SummaryItemRender";
import { TareaEjecucionProductosField } from "./TareaEjecucionProductosField";
import { TareaSelectorField } from "./TareaSelectorField";
import template from "./tareaEjecucionFormTemplate.html";

mustache.parse(template);

/** Formulario de edición de una ejecución de tarea (Render/Connect). Card
 *  "Información": 3 tiles de contexto de solo lectura (dispositivo + fechas,
 *  no viven en el request) + la tarea a asignar. Card "Productos": detalle de
 *  productos aplicados. Expone `validate()` para el modal contenedor — las
 *  tiles no participan (no son campos, no tienen error posible). */
export class TareaEjecucionFormRender implements IFormBodyRender {
  /** Tiles de contexto de solo lectura (dispositivo, fecha inicio, fecha fin). */
  private readonly _infoTiles: SummaryItemRender[];
  /** Campo selector de la tarea a asignar (autocomplete + crear + editar). */
  private readonly _tarea: TareaSelectorField;
  /** Campo de detalle de productos aplicados. */
  private readonly _productos: TareaEjecucionProductosField;
  /** Campos con validación (para `validate`; las tiles no entran acá). */
  private readonly _fields: IFormField[];

  /**
   * @param request   Request de la ejecución (lo mutan los campos).
   * @param ejecucion Ejecución original (contexto de solo lectura + detalles
   *                  iniciales del field de productos).
   * @param finca     Finca de la ejecución (resuelve el `clienteId` del
   *                  catálogo de tareas).
   */
  constructor(
    private readonly _request: TareaEjecucionRequest,
    ejecucion: TareaEjecucionResponse,
    finca: ClienteFincaBaseResponse,
  ) {
    this._infoTiles = [
      new SummaryItemRender({
        label: "Código dispositivo",
        description: "Código del dispositivo asociado a la tarea",
        value: `#${ejecucion.gpsTrackerNumero}`,
      }),
      new SummaryItemRender({
        label: "Fecha inicio",
        description: "Fecha de inicio de la tarea",
        value: this._fechaHora(ejecucion.fechaInicio),
      }),
      new SummaryItemRender({
        label: "Fecha fin",
        description: "Fecha de fin de la tarea",
        value: this._fechaHora(ejecucion.fechaFin),
      }),
    ];
    this._tarea = new TareaSelectorField(
      this._request,
      finca.clienteId,
      ejecucion.tareaNombre,
    );
    this._productos = new TareaEjecucionProductosField(
      this._request,
      ejecucion.detalles,
    );
    this._fields = [this._tarea, this._productos];
  }

  render(): string {
    return mustache.render(template, {
      infoTiles: this._infoTiles.map((tile) => tile.render()).join(""),
      tarea: this._tarea.render(),
      productos: this._productos.render(),
    });
  }

  connect(): void {
    this._infoTiles.forEach((tile) => tile.connect());
    this._fields.forEach((field) => field.connect());
  }

  dispose(): void {
    this._infoTiles.forEach((tile) => tile.dispose());
    this._fields.forEach((field) => field.dispose());
  }

  /** Valida los campos reales (tarea + productos); las tiles no participan. */
  validate(): boolean {
    let isValid = true;
    this._fields.forEach((field) => {
      field.showError();
      if (field.getError()) {
        if (isValid) field.focus();
        isValid = false;
      }
    });
    return isValid;
  }

  /** "dd/mm/aaaa HH:mm" a partir de una fecha-hora ISO (UTC → local). */
  private _fechaHora(fechaHoraIso: string): string {
    const d = new Date(fechaHoraIso);
    const fecha = d.toLocaleDateString("es-CO");
    const hora = d.toLocaleTimeString("es-CO", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${fecha} ${hora}`;
  }
}
